import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_DragAndDrop = () => {
  return (
    <div className="html-container">
      <h1>HTML Drag and Drop API</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          The <strong>HTML Drag and Drop API</strong> allows users to click and drag elements
          from one location to another within a web page. It provides a natural and interactive
          way to rearrange, move, or transfer data between elements.
        </p>
        <p>
          Drag-and-drop is implemented using JavaScript events such as{" "}
          <code>dragstart</code>, <code>dragover</code>, and <code>drop</code>.
        </p>
      </section>

      {/* 2. Basic Concepts */}
      <section className="html-section">
        <h2>2. Basic Concepts</h2>
        <ul>
          <li><strong>Draggable Elements:</strong> Elements that can be dragged using the attribute <code>draggable="true"</code>.</li>
          <li><strong>Drop Zone:</strong> The area where elements can be dropped.</li>
          <li><strong>Events:</strong> The API uses several drag events to control the process.</li>
        </ul>
      </section>

      {/* 3. Drag and Drop Events */}
      <section className="html-section">
        <h2>3. Drag and Drop Events</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>ondragstart</code></td>
              <td>Triggered when the drag starts.</td>
            </tr>
            <tr>
              <td><code>ondragover</code></td>
              <td>Triggered when a dragged element is over a valid drop target.</td>
            </tr>
            <tr>
              <td><code>ondrop</code></td>
              <td>Triggered when the dragged element is dropped on the target.</td>
            </tr>
            <tr>
              <td><code>ondragenter</code></td>
              <td>Triggered when a dragged element enters a valid drop target.</td>
            </tr>
            <tr>
              <td><code>ondragleave</code></td>
              <td>Triggered when the dragged element leaves the drop target.</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 4. Example: Basic Drag and Drop */}
      <section className="html-section">
        <h2>4. Example: Basic Drag and Drop</h2>
        <p>
          Here’s a simple example where an image can be dragged and dropped into a box.
        </p>
        <pre className="code-block">
          <code>{`<!DOCTYPE html>
<html>
<head>
  <style>
    #dropZone {
      width: 200px;
      height: 200px;
      border: 2px dashed #2196f3;
      text-align: center;
      line-height: 200px;
      margin-top: 20px;
    }
  </style>
</head>
<body>

<h3>Drag and Drop Example</h3>

<img id="drag1" src="img_logo.png" draggable="true" width="100" height="100" 
     ondragstart="drag(event)" alt="Logo">

<div id="dropZone" ondrop="drop(event)" ondragover="allowDrop(event)">
  Drop here
</div>

<script>
function allowDrop(event) {
  event.preventDefault();
}

function drag(event) {
  event.dataTransfer.setData("text", event.target.id);
}

function drop(event) {
  event.preventDefault();
  var data = event.dataTransfer.getData("text");
  event.target.appendChild(document.getElementById(data));
}
</script>

</body>
</html>`}</code>
        </pre>
      </section>

      {/* 5. Explanation */}
      <section className="html-section">
        <h2>5. Explanation of Code</h2>
        <ul>
          <li><code>draggable="true"</code> makes the image draggable.</li>
          <li><code>ondragstart</code> sets the data being dragged.</li>
          <li><code>ondragover</code> prevents default behavior to allow dropping.</li>
          <li><code>ondrop</code> handles dropping the element into the target.</li>
        </ul>
      </section>

      {/* 6. Data Transfer Object */}
      <section className="html-section">
        <h2>6. The DataTransfer Object</h2>
        <p>
          During drag-and-drop, the browser provides a <code>DataTransfer</code> object that
          holds the data being moved.
        </p>

        <table className="styled-table">
          <thead>
            <tr>
              <th>Property / Method</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>setData(format, data)</code></td>
              <td>Sets the data type and value to be dragged.</td>
            </tr>
            <tr>
              <td><code>getData(format)</code></td>
              <td>Retrieves the data being dragged.</td>
            </tr>
            <tr>
              <td><code>clearData()</code></td>
              <td>Clears all data from the drag operation.</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 7. Example: Moving Text Between Divs */}
      <section className="html-section">
        <h2>7. Example: Moving Text Between Divs</h2>
        <pre className="code-block">
          <code>{`<div id="source" draggable="true" ondragstart="drag(event)" 
     style="padding:10px; border:1px solid black;">Drag this text</div>

<div id="target" ondrop="drop(event)" ondragover="allowDrop(event)" 
     style="margin-top:20px; width:200px; height:50px; border:1px dashed gray;">
Drop here
</div>`}</code>
        </pre>
      </section>

      {/* 8. Browser Support */}
      <section className="html-section">
        <h2>8. Browser Support</h2>
        <p>
          The HTML Drag and Drop API is supported in all major browsers, including:
        </p>
        <ul>
          <li>Google Chrome ✅</li>
          <li>Microsoft Edge ✅</li>
          <li>Mozilla Firefox ✅</li>
          <li>Safari ✅</li>
          <li>Opera ✅</li>
        </ul>
      </section>

      {/* 9. Use Cases */}
      <section className="html-section">
        <h2>9. Common Use Cases</h2>
        <ul>
          <li>Rearranging items in a list or gallery.</li>
          <li>Uploading files via drag-and-drop areas.</li>
          <li>Building visual editors or dashboards.</li>
          <li>Moving data between containers.</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>Use <code>draggable="true"</code> to make elements draggable.</li>
          <li>Use <code>ondrop</code> and <code>ondragover</code> for drop targets.</li>
          <li>Use the <code>DataTransfer</code> object to manage dragged data.</li>
          <li>Drag and Drop enhances user interactivity in modern web apps.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_DragAndDrop;
